import React, { createContext, useState, useEffect, useMemo } from 'react';

export const AuthContext = createContext(null);

const transformUserData = (userData) => ({
  id: userData.cid,
  email: userData.email,
  firstName: userData.firstName,
  lastName: userData.lastName,
  username: userData.username,
  emailVerified: userData.emailVerified,
  roles: userData.roles,
  fullName: `${userData.firstName} ${userData.lastName}`,
});

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState({
    user: null,
    isLoading: true,
    isLoggedIn: false,
    userRoles: [],
  });

  const getUserInfo = () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (!storedUser || !token) return null;
      return transformUserData(JSON.parse(storedUser));
    } catch (error) {
      console.error('Error getting user info:', error);
      return null;
    }
  };

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const userInfo = getUserInfo();

      setAuthState({
        user: userInfo,
        isLoading: false,
        isLoggedIn: !!token && !!userInfo,
        userRoles: userInfo?.roles || [],
      });
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthState({
        user: null,
        isLoading: false,
        isLoggedIn: false,
        userRoles: [],
      });
    }
  };

  const login = (userData) => {
    const transformedUser = transformUserData(userData);
    setAuthState({
      user: transformedUser,
      isLoading: false,
      isLoggedIn: true,
      userRoles: transformedUser.roles || [],
    });
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthState({
      user: null,
      isLoading: false,
      isLoggedIn: false,
      userRoles: [],
    });
  };

  useEffect(() => {
    checkAuthStatus();

    const handleStorageChange = () => {
      checkAuthStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const contextValue = useMemo(
    () => ({
      ...authState,
      login,
      logout,
      getUserInfo,
      checkAuthStatus,
    }),
    [authState]
  );

  if (authState.isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
